import { CaptionPlugin } from '@platejs/caption/react';
import { TrailingBlockPlugin } from 'platejs';
import { useMemo } from 'react';

import {
  DropPlugin,
  PastePlugin,
  alignPlugin,
  backgroundColorPlugin,
  basicMarksPlugin,
  fontSizePlugin,
  imagePlugin,
  indentPlugin,
  linkPlugin,
  listComponents,
  listPlugin,
  mentionComponents,
  mentionPlugin,
  tableCellHeaderPlugin,
  tableCellPlugin,
  tablePlugin,
  tableRowPlugin,
  textColorPlugin,
} from './plugins';
import { hasToolbarFeature, type EditorToolbarPreset } from './toolbarPresets';

export const usePlugins = <
  TMentionComponentValue extends { type: string },
  TMentionComponentPropsExtension,
>({
  includeAdvancedFormatting,
  MentionLabel,
  mentionProps,
  onPasteMention,
  onStripCidImages,
  allowedImageTypes,
  allowPublicImageUpload,
  toolbarPreset = 'default',
}: {
  includeAdvancedFormatting?: boolean;

  MentionLabel?: React.FunctionComponent<
    { value: TMentionComponentValue } & TMentionComponentPropsExtension
  >;
  mentionProps?: TMentionComponentPropsExtension;
  onPasteMention?: (mention: TMentionComponentValue) => boolean;
  onStripCidImages?: () => void;
  onUploadPublicImages?: () => void;
  allowedImageTypes?: { type: string; extension: string }[];
  allowPublicImageUpload?: 'allowed' | 'required' | 'disallowed';
  toolbarPreset?: EditorToolbarPreset;
}) => {
  const includesFeature = (feature: Parameters<typeof hasToolbarFeature>[1]) =>
    includeAdvancedFormatting || hasToolbarFeature(toolbarPreset, feature);

  return useMemo(
    () => ({
      plugins: [
        basicMarksPlugin,
        listPlugin,
        ...(includesFeature('align') ? [alignPlugin] : []),
        ...(includesFeature('indent') ? [indentPlugin] : []),
        ...(includesFeature('link') ? [linkPlugin] : []),
        ...(includesFeature('table')
          ? [
              tablePlugin,
              tableRowPlugin,
              tableCellPlugin,
              tableCellHeaderPlugin,
            ]
          : []),
        ...(includesFeature('textColor') ? [textColorPlugin] : []),
        ...(includesFeature('backgroundColor') ? [backgroundColorPlugin] : []),
        ...(includesFeature('fontSize') ? [fontSizePlugin] : []),
        imagePlugin,
        CaptionPlugin.configure({
          options: {
            query: {
              allow: [imagePlugin.key],
            },
          },
        }),
        mentionPlugin.configure({
          options: {
            trigger: '@',
            query: () => false,
            insertSpaceAfterMention: true,
            MentionLabel,
            mentionProps,
          } as Record<string, unknown>,
        }),
        PastePlugin.configure({
          options: {
            onPasteMention,
            onStripCidImages,
            allowedImageTypes,
            allowPublicImageUpload,
          },
        }),
        DropPlugin.configure({
          options: {
            allowedImageTypes,
            allowPublicImageUpload,
          },
        }),
        TrailingBlockPlugin,
      ],
      components: {
        ...listComponents,
        ...mentionComponents,
      },
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
};
